import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useForm } from 'react-hook-form';
import { 
  FiMail, 
  FiPhone, 
  FiMapPin, 
  FiLinkedin,
  FiGithub,
  FiSend,
  FiCheck,
  FiClock,
  FiGlobe
} from 'react-icons/fi';
import {
  ContactContainer,
  ContactContent,
  ContactHeader,
  ContactTitle,
  ContactSubtitle,
  ContactGrid,
  ContactInfo,
  ContactMethod,
  ContactIcon,
  ContactDetails,
  ContactLabel,
  ContactValue,
  ContactForm,
  FormTitle,
  FormGroup,
  FormLabel,
  FormInput,
  FormTextarea,
  FormButton,
  FormStatus,
  LocationCard,
  LocationIcon,
  LocationDetails,
  LocationTitle,
  LocationTime,
  ResponseTime
} from './Contact.styles';
import { personalInfo } from '../../utils/constants';
import useLocale from '../../hooks/useLocale';

const Contact = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [formStatus, setFormStatus] = useState('idle'); // idle, sending, success, error
  
  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
      duration: 0.45,
      staggerChildren: 0.12
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };

  const onSubmit = async (data) => {
    setFormStatus('sending');
    
    try {
      // Simulate API call - replace with your actual contact form submission
      // Simulate API call (shorter delay for snappier UX)
      await new Promise(resolve => setTimeout(resolve, 800));
      
      console.log('Form data:', data);
      setFormStatus('success');
      reset();
      
      // Reset status after 5 seconds
      setTimeout(() => setFormStatus('idle'), 3000);
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  const { t } = useLocale();

  const contactMethods = [
    {
      icon: FiMail,
      key: 'email',
      label: t('contact.methods.email'),
      value: personalInfo.email,
      href: `mailto:${personalInfo.email}`,
      color: "#6366f1"
    },
    {
      icon: FiPhone,
      key: 'phone',
      label: t('contact.methods.phone'),
      value: personalInfo.phone,
      href: `tel:${personalInfo.phone}`,
      color: "#f59e0b"
    },
    {
      icon: FiLinkedin,
      key: 'linkedin',
      label: t('contact.methods.linkedin'),
      value: t('contact.methods.connect'),
      href: personalInfo.linkedin,
      color: "#0077b5"
    },
    {
      icon: FiGithub,
      key: 'github',
      label: t('contact.methods.github'),
      value: t('contact.methods.viewCode'),
      href: personalInfo.github,
      color: "#333"
    }
  ];

  // Get current time in Beirut
  const getCurrentTime = () => {
    const now = new Date();
    const beirutTime = now.toLocaleTimeString('en-US', {
      timeZone: 'Asia/Beirut',
      hour12: true,
      hour: 'numeric',
      minute: '2-digit'
    });
    return beirutTime;
  };

  return (
    <ContactContainer id="contact" className="section section--white">
      <ContactContent className="container">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <ContactHeader>
            <motion.div variants={itemVariants}>
              <ContactTitle>{t('contact.title')}</ContactTitle>
            </motion.div>
            <motion.div variants={itemVariants}>
              <ContactSubtitle>
                {t('contact.subtitle')}
              </ContactSubtitle>
            </motion.div>
          </ContactHeader>

          <ContactGrid>
            {/* Contact Information */}
            <ContactInfo>
              <motion.div variants={itemVariants}>
                <LocationCard
                  as={motion.div}
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.3 }}
                >
                  <LocationIcon>
                    <FiMapPin />
                  </LocationIcon>
                  <LocationDetails>
                    <LocationTitle>{personalInfo.location}</LocationTitle>
                    <LocationTime>
                      <FiClock />
                      {t('contact.localTime')}: {getCurrentTime()}
                    </LocationTime>
                  </LocationDetails>
                </LocationCard>
              </motion.div>

              <motion.div variants={itemVariants}>
                <ResponseTime>
                  <FiGlobe />
                  <span>{t('contact.responseTime')}</span>
                </ResponseTime>
              </motion.div>

              {contactMethods.map((method, index) => (
                <motion.div key={index} variants={itemVariants}>
                  <ContactMethod
                    as={motion.a}
                    href={method.href}
                    target={method.label === 'LinkedIn' || method.label === 'GitHub' ? '_blank' : undefined}
                    rel={method.label === 'LinkedIn' || method.label === 'GitHub' ? 'noopener noreferrer' : undefined}
                    whileHover={{ x: 10, scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ContactIcon $color={method.color}>
                      <method.icon />
                    </ContactIcon>
                    <ContactDetails>
                      <ContactLabel>{method.label}</ContactLabel>
                      <ContactValue>{method.value}</ContactValue>
                    </ContactDetails>
                  </ContactMethod>
                </motion.div>
              ))}

            </ContactInfo>

            {/* Contact Form */}
            <ContactForm
              as={motion.form}
              variants={itemVariants}
              onSubmit={handleSubmit(onSubmit)}
            >
              <FormTitle>{t('contact.form.title')}</FormTitle>
              
              <FormGroup>
                <FormLabel>{t('contact.form.labels.name')} *</FormLabel>
                <FormInput
                  type="text"
                  placeholder={t('contact.form.placeholders.name')}
                  {...register('name', { 
                    required: t('contact.form.validation.nameRequired'),
                    minLength: { value: 2, message: t('contact.form.validation.nameMin') }
                  })}
                  $hasError={!!errors.name}
                />
                {errors.name && (
                  <span style={{ color: '#ef4444', fontSize: '0.875rem' }}>
                    {errors.name.message}
                  </span>
                )}
              </FormGroup>

              <FormGroup>
                <FormLabel>{t('contact.form.labels.email')} *</FormLabel>
                <FormInput
                  type="email"
                  placeholder={t('contact.form.placeholders.email')}
                  {...register('email', { 
                    required: t('contact.form.validation.emailRequired'),
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: t('contact.form.validation.emailInvalid')
                    }
                  })}
                  $hasError={!!errors.email}
                />
                {errors.email && (
                  <span style={{ color: '#ef4444', fontSize: '0.875rem' }}>
                    {errors.email.message}
                  </span>
                )}
              </FormGroup>

              <FormGroup>
                <FormLabel>{t('contact.form.labels.subject')}</FormLabel>
                <FormInput
                  type="text"
                  placeholder={t('contact.form.placeholders.subject')}
                  {...register('subject')}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>{t('contact.form.labels.message')} *</FormLabel>
                <FormTextarea
                  placeholder={t('contact.form.placeholders.message')}
                  rows="5"
                  {...register('message', { 
                    required: t('contact.form.validation.messageRequired'),
                    minLength: { value: 10, message: t('contact.form.validation.messageMin') }
                  })}
                  $hasError={!!errors.message}
                />
                {errors.message && (
                  <span style={{ color: '#ef4444', fontSize: '0.875rem' }}>
                    {errors.message.message}
                  </span>
                )}
              </FormGroup>

              <FormButton
                type="submit"
                as={motion.button}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={formStatus === 'sending'}
                $status={formStatus}
              >
                {formStatus === 'sending' && <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                >
                  <FiClock />
                </motion.div>}
                {formStatus === 'success' && <FiCheck />}
                {formStatus === 'idle' && <FiSend />}
                {formStatus === 'error' && <FiSend />}
                
                {formStatus === 'sending' && t('contact.form.buttons.sending')}
                {formStatus === 'success' && t('contact.form.buttons.sent')}
                {formStatus === 'error' && t('contact.form.buttons.error')}
                {formStatus === 'idle' && t('contact.form.buttons.send')}
              </FormButton>

              {formStatus !== 'idle' && (
                <FormStatus
                  as={motion.div}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  $status={formStatus}
                >
                  {formStatus === 'success' && t('contact.form.status.success')}
                  {formStatus === 'error' && t('contact.form.status.error')}
                  {formStatus === 'sending' && t('contact.form.status.sending')}
                </FormStatus>
              )}
            </ContactForm>
          </ContactGrid>
        </motion.div>
      </ContactContent>
    </ContactContainer>
  );
};

export default Contact;